// ============================================================================
//  Insurance validation rules
// ============================================================================
//
//  One place for the rules that read an insurance company's setup row, because
//  the screens that enforce them are duplicated — Record/PatientInformation and
//  Emergency/Records/PatientInformation are near-identical copies, and a rule
//  written twice is a rule that will eventually be two different rules.
//
//  The insurance row comes straight from `SELECT * FROM insurance`, so the
//  field names here are the raw column names.

// ── Membership number length ────────────────────────────────────────────────
//
//  `member_count` is how many characters a membership number must have for the
//  scheme. Not every scheme has a fixed format: some issue numbers of varying
//  length, and for those there is nothing to check against.
//
//  0 is the stored sentinel for "no fixed length" — it is what the "Accept any
//  length" option on the Insurance setup screen writes. NULL and '' mean the
//  same thing and are treated identically, which matters because a blank field
//  on an INT column already lands in the database as 0, so schemes configured
//  before this option existed read back correctly without a migration.
//
//  Returns the required length, or 0 when the scheme accepts any length.
export const membershipLength = (insurance) => {
  const n = Number(insurance?.member_count);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const acceptsAnyMembershipLength = (insurance) => membershipLength(insurance) === 0;

// The error message for a membership number that is the wrong length, or null
// when it is acceptable. Only the LENGTH is judged here — whether the number is
// required at all is a separate question the caller has already answered.
export const membershipLengthError = (insurance, value) => {
  const required = membershipLength(insurance);
  if (!required) return null;                    // scheme accepts any length

  const entered = String(value ?? '').trim();
  if (!entered) return null;                     // "required" is checked elsewhere
  if (entered.length === required) return null;

  return `Membership Number must be ${required} characters (${entered.length} entered)`;
};

// ── Claim check code ────────────────────────────────────────────────────────
//
//  `ccc` is the "Claim Check Code Required" toggle, stored as the string
//  'Required' (older rows carry '1'). `cccc` is how many characters the code
//  has.
export const cccRequired = (insurance) => {
  const flag = String(insurance?.ccc ?? '').trim();
  return flag === 'Required' || flag === '1';
};

export const cccLength = (insurance) => {
  const n = Number(insurance?.cccc);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Length check for a claim check code. Deliberately skipped when the scheme
// declares no count: the previous inline check compared against `cccc`
// unconditionally, so a scheme with CCC required but no count configured
// rejected every code ever typed into it ('5'.length != '' is always true).
export const cccLengthError = (insurance, value) => {
  const required = cccLength(insurance);
  if (!required) return null;

  const entered = String(value ?? '').trim();
  if (!entered) return null;                     // "required" is checked elsewhere
  if (entered.length === required) return null;

  return `CCC must be ${required} characters (${entered.length} entered)`;
};

// ── The whole rule set for one scheme ───────────────────────────────────────
//
//  What the registration screens ask for the moment an insurance is picked:
//  which fields accept input, which are mandatory, how long their values must
//  be, and the line of text under each explaining it. Everything here comes
//  from the scheme's setup row — the point is that the Insurance settings
//  screen is the only place these behaviours are decided.
//
//  Pass `undefined` (nothing selected yet) and every field reports itself
//  disabled, which is what keeps staff from typing a membership number before
//  there is a scheme to validate it against.
export const insuranceRulesFor = (insurance) => {
  const selected = !!insurance;
  const name     = String(insurance?.name || '');

  // "Non Insured" is a real row in the insurance table, not a special case in
  // the schema — but a patient with no scheme has no membership number to
  // give, so it cannot be mandatory for them.
  const isNonInsured = name.trim().toLowerCase() === 'non insured';

  const memberLen = membershipLength(insurance);
  const needsCcc  = selected && cccRequired(insurance);
  const codeLen   = cccLength(insurance);

  return {
    selected,
    name,
    isNonInsured,

    membership: {
      enabled:   selected,
      required:  selected && !isNonInsured,
      length:    memberLen,              // 0 → any length
      anyLength: memberLen === 0,
      hint: !selected
        ? 'Select an insurance first'
        : isNonInsured
          ? 'Not required for uninsured patients'
          : memberLen
            ? `Must be exactly ${memberLen} characters`
            : 'Any length accepted',
    },

    ccc: {
      // Not required means not enterable. A code typed against a scheme that
      // does not use one is noise at best, and it would travel into the claim.
      enabled:  needsCcc,
      required: needsCcc,
      length:   codeLen,
      hint: !selected
        ? 'Select an insurance first'
        : !needsCcc
          ? `${name || 'This scheme'} does not use a claim check code`
          : codeLen
            ? `Must be exactly ${codeLen} characters`
            : 'Required for every visit',
    },
  };
};
